import React, { useRef } from 'react'
import classNames from 'classnames'
import { IMaskInput } from 'react-imask'
import { useTranslation } from 'react-i18next'
import { Row, Col, Form, Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { ErrorMessage, getIn, useFormikContext } from 'formik'
import { CreateApplicationFormValues } from './CreateApplication'
import { RootState } from '../store/rootReducer'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { searchMyFamily } from '../api/registration'
import { KENNITALA_LENGTH, isAdultAge, normalizeSsn } from '../utils/formUtils'

interface Props {
  index: number
}

const CreateApplicationContact = ({ index }: Props): React.ReactElement | null => {
  const { currentUser } = useSelector((state: RootState) => state.auth)
  const { t } = useTranslation(['createApplication', 'common'])
  const { values, errors, touched, getFieldProps, setFieldValue, setFieldTouched } =
    useFormikContext<CreateApplicationFormValues>()
  const valuesRef = useRef(values)
  valuesRef.current = values

  if (currentUser == null) {
    return null
  }

  const currentUserSsn = normalizeSsn(currentUser.ssn)
  const applicantSsn = normalizeSsn(values.ssn)
  const contactSsn = normalizeSsn(values.contacts[index]?.ssn)
  const applicantIsCurrentUser = applicantSsn === currentUserSsn
  const isLockedCurrentUserContact = !applicantIsCurrentUser && contactSsn === currentUserSsn
  const canDelete = !isLockedCurrentUserContact && (isAdultAge(values.ageNow) || values.contacts.length > 1)

  return (
    <div>
      <Row>
        <Col sm={12} md={6}>
          <Form.Group controlId={`contacts[${index}].ssn`}>
            <Form.Label>
              {t('kennitala')}*:{' '}
              {canDelete && (
                <Button
                  className="p-0"
                  variant="link"
                  onClick={() => {
                    setFieldValue(
                      'contacts',
                      values.contacts.filter((_, i) => i !== index)
                    )
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              )}
            </Form.Label>
            <IMaskInput
              name={`contacts[${index}].ssn`}
              className={classNames('form-control', {
                'is-invalid': getIn(touched, `contacts[${index}].ssn`) && getIn(errors, `contacts[${index}].ssn`),
              })}
              mask={'000000-0000'}
              value={values.contacts[index].ssn}
              unmask={true}
              disabled={isLockedCurrentUserContact}
              onAccept={(value: string) => {
                setFieldValue(`contacts[${index}].ssn`, value)
                const normalized = normalizeSsn(value)
                if (
                  normalized.length === KENNITALA_LENGTH &&
                  normalized !== applicantSsn &&
                  !values.contacts[index]?.name
                ) {
                  searchMyFamily(normalized)
                    .then((r) => {
                      const current = valuesRef.current.contacts[index]
                      if (r.data?.name && normalizeSsn(current?.ssn) === normalized && !current?.name) {
                        setFieldValue(`contacts[${index}].name`, r.data.name)
                      }
                    })
                    .catch((e) => {
                      if (!('isAxiosError' in e && e.response && e.response.status === 400)) {
                        toast.error(t('common:kerfisvilla'))
                      }
                    })
                }
              }}
              onBlur={() => {
                setFieldTouched(`contacts[${index}].ssn`)
              }}
            />
            <Form.Text className="text-muted">{t('kennitalaAdstandanda')}</Form.Text>
            <ErrorMessage
              name={`contacts[${index}].ssn`}
              component="div"
              className="invalid-feedback d-block text-left"
            />
          </Form.Group>
        </Col>
        <Col sm={12} md={6}>
          <Form.Group controlId={`contacts[${index}].name`}>
            <Form.Label>{t('nafn')}*:</Form.Label>
            <Form.Control
              type="text"
              className={classNames({
                'is-invalid': getIn(touched, `contacts[${index}].name`) && getIn(errors, `contacts[${index}].name`),
              })}
              {...getFieldProps(`contacts[${index}].name`)}
            />
            <Form.Text className="text-muted">{t('nafnAdstandanda')}</Form.Text>
            <ErrorMessage
              name={`contacts[${index}].name`}
              component="div"
              className="invalid-feedback d-block text-left"
            />
          </Form.Group>
        </Col>
        <Col sm={12} md={6}>
          <Form.Group controlId={`contacts[${index}].email`}>
            <Form.Label>{t('netfang')}*:</Form.Label>
            <Form.Control
              type="email"
              className={classNames({
                'is-invalid': getIn(touched, `contacts[${index}].email`) && getIn(errors, `contacts[${index}].email`),
              })}
              {...getFieldProps(`contacts[${index}].email`)}
            />
            <Form.Text className="text-muted">{t('netfangAdstandanda')}</Form.Text>
            <ErrorMessage
              name={`contacts[${index}].email`}
              component="div"
              className="invalid-feedback d-block text-left"
            />
          </Form.Group>
        </Col>
        <Col sm={12} md={6}>
          <Form.Group controlId={`contacts[${index}].mobile`}>
            <Form.Label>{t('farsimi')}*:</Form.Label>
            <Form.Control
              type="text"
              className={classNames({
                'is-invalid': getIn(touched, `contacts[${index}].mobile`) && getIn(errors, `contacts[${index}].mobile`),
              })}
              {...getFieldProps(`contacts[${index}].mobile`)}
            />
            <Form.Text className="text-muted">{t('farsimiAdstandanda')}</Form.Text>
            <ErrorMessage
              name={`contacts[${index}].mobile`}
              component="div"
              className="invalid-feedback d-block text-left"
            />
          </Form.Group>
        </Col>
      </Row>
      <hr />
    </div>
  )
}

export default CreateApplicationContact
